import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Hash, Users, Send, RefreshCw, MessageSquare,
  Loader2, AlertCircle, CheckCircle2, User, ExternalLink, Image as ImageIcon,
  Star, EyeOff, Eye, SortAsc, SortDesc, Filter, ChevronDown, Paperclip, X, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API = typeof window !== 'undefined' ? window.location.origin : '';

// Local storage keys
const HIDDEN_CHANNELS_KEY = 'slack_hidden_channels';
const FAVORITE_CHANNELS_KEY = 'slack_favorite_channels';

export default function SlackPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('channels');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  
  // File attachment state
  const [attachedFile, setAttachedFile] = useState(null);
  const [attachedFilePreview, setAttachedFilePreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Thread/replies state
  const [activeThread, setActiveThread] = useState(null); // { ts, channel_id }
  const [threadMessages, setThreadMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadReply, setThreadReply] = useState('');
  
  // Mention state
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  
  // Filtering and sorting state
  const [channelSort, setChannelSort] = useState('favorites'); // 'favorites' | 'a-z' | 'z-a' | 'newest' | 'oldest'
  const [memberSort, setMemberSort] = useState('a-z'); // 'a-z' | 'z-a'
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenChannels, setHiddenChannels] = useState(() => {
    try {
      const stored = localStorage.getItem(HIDDEN_CHANNELS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [favoriteChannels, setFavoriteChannels] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITE_CHANNELS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showMemberSortMenu, setShowMemberSortMenu] = useState(false);

  // Create a user lookup map for fast access
  const userMap = useMemo(() => {
    const map = {};
    users.forEach(u => {
      map[u.id] = u;
    });
    return map;
  }, [users]);
  
  // Sorted and filtered channels
  const sortedChannels = useMemo(() => {
    let filtered = [...channels];
    
    // Filter hidden channels unless showHidden is true
    if (!showHidden) {
      filtered = filtered.filter(c => !hiddenChannels.includes(c.id));
    }
    
    // Sort channels
    switch (channelSort) {
      case 'favorites':
        // Favorites first, then alphabetical
        filtered.sort((a, b) => {
          const aFav = favoriteChannels.includes(a.id);
          const bFav = favoriteChannels.includes(b.id);
          if (aFav && !bFav) return -1;
          if (!aFav && bFav) return 1;
          return a.name.localeCompare(b.name);
        });
        break;
      case 'a-z':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'z-a':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        filtered.sort((a, b) => (b.created || 0) - (a.created || 0));
        break;
      case 'oldest':
        filtered.sort((a, b) => (a.created || 0) - (b.created || 0));
        break;
      default:
        break;
    }
    
    return filtered;
  }, [channels, channelSort, hiddenChannels, favoriteChannels, showHidden]);
  
  // Sorted members
  const sortedMembers = useMemo(() => {
    let sorted = [...users];
    
    switch (memberSort) {
      case 'a-z':
        sorted.sort((a, b) => (a.real_name || a.name || '').localeCompare(b.real_name || b.name || ''));
        break;
      case 'z-a':
        sorted.sort((a, b) => (b.real_name || b.name || '').localeCompare(a.real_name || a.name || ''));
        break;
      default:
        break;
    }
    
    return sorted;
  }, [users, memberSort]);
  
  // Filtered users for mention picker
  const mentionFilteredUsers = useMemo(() => {
    if (!mentionSearch) return users.slice(0, 10);
    const search = mentionSearch.toLowerCase();
    return users
      .filter(u => 
        (u.real_name || '').toLowerCase().includes(search) ||
        (u.name || '').toLowerCase().includes(search) ||
        (u.display_name || '').toLowerCase().includes(search)
      )
      .slice(0, 10);
  }, [users, mentionSearch]);

  // Save hidden/favorite channels to localStorage
  useEffect(() => {
    localStorage.setItem(HIDDEN_CHANNELS_KEY, JSON.stringify(hiddenChannels));
  }, [hiddenChannels]);
  
  useEffect(() => {
    localStorage.setItem(FAVORITE_CHANNELS_KEY, JSON.stringify(favoriteChannels));
  }, [favoriteChannels]);

  // Load status on mount
  useEffect(() => {
    loadStatus();
    loadChannels();
    loadUsers();
  }, []);

  // Scroll to bottom when messages change
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (messages.length > 0) {
      // Use instant scroll on initial load, smooth scroll for new messages
      messagesEndRef.current?.scrollIntoView({ 
        behavior: isInitialLoad.current ? 'instant' : 'smooth' 
      });
      isInitialLoad.current = false;
    }
  }, [messages]);

  // Reset initial load flag when channel changes
  useEffect(() => {
    isInitialLoad.current = true;
  }, [selectedChannel]);

  // Auto-select the first channel when channels are loaded
  useEffect(() => {
    if (sortedChannels.length > 0 && !selectedChannel) {
      const firstChannel = sortedChannels[0];
      setSelectedChannel(firstChannel);
      loadMessages(firstChannel.id);
    }
  }, [sortedChannels]);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/api/slack/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load Slack status:', err);
      setStatus({ connected: false, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const loadChannels = async () => {
    try {
      const res = await fetch(`${API}/api/slack/channels`);
      const data = await res.json();
      setChannels(data.channels || []);
    } catch (err) {
      console.error('Failed to load channels:', err);
      toast.error('Failed to load channels');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await fetch(`${API}/api/slack/users`);
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const loadMessages = async (channelId) => {
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API}/api/slack/channels/${channelId}/messages?limit=50`);
      const data = await res.json();
      // Reverse to show oldest first
      setMessages((data.messages || []).reverse());
    } catch (err) {
      console.error('Failed to load messages:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Load thread replies
  const loadThread = async (channelId, threadTs) => {
    setLoadingThread(true);
    setActiveThread({ ts: threadTs, channel_id: channelId });
    try {
      const res = await fetch(`${API}/api/slack/channels/${channelId}/threads/${threadTs}`);
      const data = await res.json();
      setThreadMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to load thread:', err);
      toast.error('Failed to load thread replies');
    } finally {
      setLoadingThread(false);
    }
  };

  // Close thread panel
  const closeThread = () => {
    setActiveThread(null);
    setThreadMessages([]);
    setThreadReply('');
  };

  // Send reply to thread
  const sendThreadReply = async () => {
    if (!threadReply.trim() || !activeThread) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API}/api/slack/channels/${activeThread.channel_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: threadReply,
          thread_ts: activeThread.ts
        }),
      });
      
      if (!res.ok) throw new Error('Failed to send reply');
      
      setThreadReply('');
      // Reload thread to show new reply
      await loadThread(activeThread.channel_id, activeThread.ts);
      // Also reload main messages to update reply count
      if (selectedChannel) {
        loadMessages(selectedChannel.id);
      }
      toast.success('Reply sent');
    } catch (err) {
      console.error('Failed to send reply:', err);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const selectChannel = (channel) => {
    setSelectedChannel(channel);
    loadMessages(channel.id);
    closeThread(); // Close any open thread when switching channels
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 10MB for Slack)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setAttachedFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachedFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachedFilePreview(null);
    }
  };
  
  // Remove attached file
  const removeAttachment = () => {
    setAttachedFile(null);
    setAttachedFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle message input change to detect @ mentions
  const handleMessageChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);
    
    // Check if we're typing a mention
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      setShowMentionPicker(true);
      setMentionSearch(atMatch[1]);
      setMentionCursorPos(cursorPos);
    } else {
      setShowMentionPicker(false);
      setMentionSearch('');
    }
  };
  
  // Insert mention into message
  const insertMention = (user) => {
    const textBeforeCursor = newMessage.slice(0, mentionCursorPos);
    const textAfterCursor = newMessage.slice(mentionCursorPos);
    
    // Find the @ symbol position
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const beforeAt = textBeforeCursor.slice(0, atIndex);
    
    // Insert the mention in Slack format <@USER_ID>
    const mention = `<@${user.id}>`;
    const newText = beforeAt + mention + ' ' + textAfterCursor;
    
    setNewMessage(newText);
    setShowMentionPicker(false);
    setMentionSearch('');
    
    // Focus back on input
    messageInputRef.current?.focus();
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && !attachedFile) || !selectedChannel) return;
    
    setSending(true);
    setUploadProgress(0);
    
    try {
      if (attachedFile) {
        // Upload file with optional message
        const formData = new FormData();
        formData.append('file', attachedFile);
        formData.append('message', newMessage.trim());
        
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        
        await new Promise((resolve, reject) => {
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              try {
                const err = JSON.parse(xhr.responseText);
                reject(new Error(err.detail || 'Upload failed'));
              } catch {
                reject(new Error('Upload failed'));
              }
            }
          };
          xhr.onerror = () => reject(new Error('Network error'));
          
          xhr.open('POST', `${API}/api/slack/channels/${selectedChannel.id}/upload`);
          xhr.send(formData);
        });
        
        toast.success('File uploaded!');
        removeAttachment();
      } else {
        // Send text-only message
        const res = await fetch(`${API}/api/slack/channels/${selectedChannel.id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: newMessage })
        });
        
        if (!res.ok) throw new Error('Failed to send');
        toast.success('Message sent!');
      }
      
      setNewMessage('');
      loadMessages(selectedChannel.id);
    } catch (err) {
      console.error('Failed to send message:', err);
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  // Delete a message
  const deleteMessage = async (messageTs) => {
    if (!selectedChannel || !messageTs) return;
    
    if (!window.confirm('Delete this message? This cannot be undone.')) {
      return;
    }
    
    try {
      const res = await fetch(
        `${API}/api/slack/channels/${selectedChannel.id}/messages/${messageTs}`,
        { method: 'DELETE' }
      );
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      
      toast.success('Message deleted');
      // Remove from local state immediately for instant feedback
      setMessages(prev => prev.filter(m => m.ts !== messageTs));
    } catch (err) {
      console.error('Failed to delete message:', err);
      toast.error(err.message || 'Failed to delete message');
    }
  };

  // Toggle channel hide/show
  const toggleHideChannel = (channelId, e) => {
    e.stopPropagation();
    setHiddenChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
  };
  
  // Toggle channel favorite
  const toggleFavoriteChannel = (channelId, e) => {
    e.stopPropagation();
    setFavoriteChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(id => id !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
  };

  // Common Slack emoji name to Unicode emoji mapping
  const emojiMap = {
    // Reactions commonly used
    'pushpin': '📌',
    'alarm_clock': '⏰',
    'alarm': '⏰',
    'bell': '🔔',
    'thumbsup': '👍',
    '+1': '👍',
    'thumbsdown': '👎',
    '-1': '👎',
    'heart': '❤️',
    'fire': '🔥',
    'rocket': '🚀',
    'eyes': '👀',
    'tada': '🎉',
    'party_popper': '🎉',
    'clap': '👏',
    'raised_hands': '🙌',
    'pray': '🙏',
    'muscle': '💪',
    'ok_hand': '👌',
    'wave': '👋',
    'star': '⭐',
    'sparkles': '✨',
    'check': '✅',
    'white_check_mark': '✅',
    'heavy_check_mark': '✔️',
    'x': '❌',
    'warning': '⚠️',
    'question': '❓',
    'exclamation': '❗',
    'bulb': '💡',
    'memo': '📝',
    'pencil': '✏️',
    'speech_balloon': '💬',
    'thought_balloon': '💭',
    'calendar': '📅',
    'clock': '🕐',
    'hourglass': '⏳',
    'email': '📧',
    'phone': '📞',
    'laptop': '💻',
    'computer': '🖥️',
    'link': '🔗',
    'lock': '🔒',
    'key': '🔑',
    'mag': '🔍',
    'chart_with_upwards_trend': '📈',
    'chart_with_downwards_trend': '📉',
    'money_with_wings': '💸',
    'dollar': '💵',
    'moneybag': '💰',
    'trophy': '🏆',
    'medal': '🏅',
    'crown': '👑',
    'gem': '💎',
    'rainbow': '🌈',
    'sunny': '☀️',
    'cloud': '☁️',
    'umbrella': '☂️',
    'snowflake': '❄️',
    'zap': '⚡',
    'boom': '💥',
    'collision': '💥',
    'sweat_drops': '💦',
    'dash': '💨',
    'poop': '💩',
    'see_no_evil': '🙈',
    'hear_no_evil': '🙉',
    'speak_no_evil': '🙊',
    'monkey_face': '🐵',
    'dog': '🐶',
    'cat': '🐱',
    'lion': '🦁',
    'tiger': '🐯',
    'horse': '🐴',
    'unicorn': '🦄',
    'pig': '🐷',
    'frog': '🐸',
    'chicken': '🐔',
    'penguin': '🐧',
    'bird': '🐦',
    'eagle': '🦅',
    'butterfly': '🦋',
    'bug': '🐛',
    'bee': '🐝',
    'turtle': '🐢',
    'snake': '🐍',
    'dragon': '🐉',
    'whale': '🐳',
    'dolphin': '🐬',
    'fish': '🐟',
    'octopus': '🐙',
    'crab': '🦀',
    'shrimp': '🦐',
    'bouquet': '💐',
    'rose': '🌹',
    'sunflower': '🌻',
    'cherry_blossom': '🌸',
    'tree': '🌳',
    'palm_tree': '🌴',
    'cactus': '🌵',
    'apple': '🍎',
    'banana': '🍌',
    'grapes': '🍇',
    'watermelon': '🍉',
    'strawberry': '🍓',
    'peach': '🍑',
    'pizza': '🍕',
    'hamburger': '🍔',
    'fries': '🍟',
    'hotdog': '🌭',
    'taco': '🌮',
    'burrito': '🌯',
    'coffee': '☕',
    'tea': '🍵',
    'beer': '🍺',
    'wine_glass': '🍷',
    'cocktail': '🍸',
    'cake': '🎂',
    'cookie': '🍪',
    'chocolate_bar': '🍫',
    'candy': '🍬',
    'popcorn': '🍿',
    'ice_cream': '🍨',
    'doughnut': '🍩',
    // Faces
    'smile': '😄',
    'grinning': '😀',
    'joy': '😂',
    'laughing': '😆',
    'wink': '😉',
    'blush': '😊',
    'heart_eyes': '😍',
    'kissing_heart': '😘',
    'stuck_out_tongue': '😛',
    'stuck_out_tongue_winking_eye': '😜',
    'sunglasses': '😎',
    'smirk': '😏',
    'neutral_face': '😐',
    'expressionless': '😑',
    'unamused': '😒',
    'sweat': '😓',
    'pensive': '😔',
    'confused': '😕',
    'confounded': '😖',
    'disappointed': '😞',
    'worried': '😟',
    'angry': '😠',
    'rage': '😡',
    'cry': '😢',
    'sob': '😭',
    'scream': '😱',
    'fearful': '😨',
    'cold_sweat': '😰',
    'hushed': '😯',
    'flushed': '😳',
    'dizzy_face': '😵',
    'mask': '😷',
    'sleeping': '😴',
    'zzz': '💤',
    'thinking': '🤔',
    'thinking_face': '🤔',
    'face_with_rolling_eyes': '🙄',
    'zipper_mouth_face': '🤐',
    'nerd_face': '🤓',
    'upside_down_face': '🙃',
    'money_mouth_face': '🤑',
    'hugging_face': '🤗',
    'skull': '💀',
    'ghost': '👻',
    'alien': '👽',
    'robot_face': '🤖',
    'smiley': '😃',
    'slightly_smiling_face': '🙂',
  };

  // Convert Slack emoji name to actual emoji
  const getEmoji = (name) => {
    // Check our mapping first
    if (emojiMap[name]) return emojiMap[name];
    
    // Try skin tone variations (remove skin tone suffix)
    const baseName = name.replace(/::skin-tone-\d$/, '');
    if (emojiMap[baseName]) return emojiMap[baseName];
    
    // Return the name wrapped in colons as fallback
    return `:${name}:`;
  };

  // Get user info by ID
  const getUser = (userId) => {
    return userMap[userId] || null;
  };

  const getUserName = (userId) => {
    const user = getUser(userId);
    return user?.real_name || user?.display_name || user?.name || userId;
  };

  const getUserAvatar = (userId) => {
    const user = getUser(userId);
    const avatar = user?.avatar || null;
    // Proxy Slack avatars if they're private URLs
    if (avatar && (avatar.includes('avatars.slack-edge.com') || avatar.includes('secure.gravatar.com'))) {
      return avatar; // These are public URLs, no proxy needed
    }
    if (avatar && avatar.includes('slack')) {
      return `${API}/api/slack/proxy-image?url=${encodeURIComponent(avatar)}`;
    }
    return avatar;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(parseFloat(ts) * 1000);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
           date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Parse message text to render links, mentions, and formatting
  const renderMessageText = (text) => {
    if (!text) return null;
    
    const parts = [];
    let lastIndex = 0;
    
    // Combined regex for URLs, user mentions, channel mentions, and special links
    const regex = /<(https?:\/\/[^|>]+)(?:\|([^>]+))?>/g;
    const mentionRegex = /<@([A-Z0-9]+)(?:\|([^>]+))?>/g;
    const channelRegex = /<#([A-Z0-9]+)(?:\|([^>]+))?>/g;
    
    let fullText = text;
    
    // Replace user mentions first
    fullText = fullText.replace(mentionRegex, (match, userId, displayName) => {
      const user = getUser(userId);
      const name = displayName || user?.real_name || user?.display_name || user?.name || userId;
      return `__MENTION__${userId}__${name}__ENDMENTION__`;
    });
    
    // Replace channel mentions
    fullText = fullText.replace(channelRegex, (match, channelId, channelName) => {
      const name = channelName || channelId;
      return `__CHANNEL__${channelId}__${name}__ENDCHANNEL__`;
    });
    
    // Now parse the text for links and our special markers
    const tokens = fullText.split(/(__MENTION__|__ENDMENTION__|__CHANNEL__|__ENDCHANNEL__)/);
    
    let i = 0;
    while (i < tokens.length) {
      const token = tokens[i];
      
      if (token === '__MENTION__') {
        // Next token is userId__name
        const data = tokens[i + 1];
        const [userId, ...nameParts] = data.split('__');
        const name = nameParts.join('__');
        parts.push(
          <span key={`mention-${i}`} className="text-blue-400 bg-blue-500/20 px-1 rounded font-medium">
            @{name}
          </span>
        );
        i += 3; // Skip __MENTION__, data, __ENDMENTION__
        continue;
      }
      
      if (token === '__CHANNEL__') {
        const data = tokens[i + 1];
        const [channelId, ...nameParts] = data.split('__');
        const name = nameParts.join('__');
        parts.push(
          <span key={`channel-${i}`} className="text-blue-400 bg-blue-500/20 px-1 rounded font-medium">
            #{name}
          </span>
        );
        i += 3;
        continue;
      }
      
      if (token === '__ENDMENTION__' || token === '__ENDCHANNEL__') {
        i++;
        continue;
      }
      
      // Parse URLs in regular text
      const urlRegex = /<(https?:\/\/[^|>]+)(?:\|([^>]+))?>/g;
      let urlMatch;
      let lastUrlIndex = 0;
      const textParts = [];
      
      while ((urlMatch = urlRegex.exec(token)) !== null) {
        // Add text before the URL
        if (urlMatch.index > lastUrlIndex) {
          textParts.push(token.slice(lastUrlIndex, urlMatch.index));
        }
        
        const url = urlMatch[1];
        const label = urlMatch[2] || url;
        textParts.push(
          <a
            key={`url-${i}-${urlMatch.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline inline-flex items-center gap-1"
          >
            {label.length > 60 ? label.slice(0, 60) + '...' : label}
            <ExternalLink size={12} className="inline flex-shrink-0" />
          </a>
        );
        
        lastUrlIndex = urlMatch.index + urlMatch[0].length;
      }
      
      // Add remaining text
      if (lastUrlIndex < token.length) {
        textParts.push(token.slice(lastUrlIndex));
      }
      
      if (textParts.length > 0) {
        parts.push(...textParts.map((p, idx) => 
          typeof p === 'string' ? <span key={`text-${i}-${idx}`}>{p}</span> : p
        ));
      }
      
      i++;
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Helper to get proxied image URL for Slack private images
  const getProxiedImageUrl = (url) => {
    if (!url) return null;
    
    // Always proxy Slack file URLs - they require authentication
    // This covers: files.slack.com, slack-files.com, slack-edge.com, slack-imgs.com
    if (url.includes('slack.com') || 
        url.includes('slack-files') || 
        url.includes('slack-edge') || 
        url.includes('slack-imgs') ||
        url.includes('slack.')) {
      return `${API}/api/slack/proxy-image?url=${encodeURIComponent(url)}`;
    }
    
    return url;
  };

  // Render attached files/images
  const renderAttachments = (msg) => {
    const items = [];
    
    // Handle files array
    if (msg.files?.length > 0) {
      msg.files.forEach((file, idx) => {
        // Check if it's an image by mimetype or file extension
        const isImage = file.mimetype?.startsWith('image/') || 
                        /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(file.name || '');
        
        if (isImage) {
          // Try multiple URL sources for the image
          // Slack provides various thumbnail sizes and URLs
          const imageUrl = file.url_private || 
                          file.thumb_720 || 
                          file.thumb_480 || 
                          file.thumb_360 || 
                          file.thumb_160 ||
                          file.permalink;
          
          const fullUrl = file.url_private || file.permalink;
          
          if (!imageUrl) {
            // No image URL available, show as file link
            items.push(
              <a
                key={`file-${idx}`}
                href={file.permalink_public || file.permalink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/20 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ImageIcon size={14} />
                <span className="text-sm">{file.name || 'Image'}</span>
              </a>
            );
            return;
          }
          
          const proxiedSrc = getProxiedImageUrl(imageUrl);
          const proxiedFull = getProxiedImageUrl(fullUrl);
          
          items.push(
            <a
              key={`file-${idx}`}
              href={proxiedFull}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2"
            >
              <img
                src={proxiedSrc}
                alt={file.name || 'Image'}
                className="max-w-md max-h-80 rounded-lg border border-white/20 hover:border-purple-500/50 transition-colors object-contain bg-black/20"
                loading="lazy"
                onError={(e) => {
                  console.error('Image load error for:', file.name, 'URL:', imageUrl);
                  // Show placeholder on error
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  // Show fallback text
                  const fallback = document.createElement('div');
                  fallback.className = 'mt-2 inline-flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm';
                  fallback.innerHTML = `<span>⚠️ ${file.name || 'Image'} (failed to load)</span>`;
                  e.target.parentNode.appendChild(fallback);
                }}
              />
              {file.name && (
                <span className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <ImageIcon size={12} />
                  {file.name}
                </span>
              )}
            </a>
          );
        } else {
          // Non-image file
          items.push(
            <a
              key={`file-${idx}`}
              href={file.permalink_public || file.url_private || file.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/20 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ExternalLink size={14} />
              <span className="text-sm">{file.name || 'File'}</span>
            </a>
          );
        }
      });
    }
    
    // Handle attachments array (Slack rich attachments)
    if (msg.attachments?.length > 0) {
      msg.attachments.forEach((att, idx) => {
        // Handle image attachments
        if (att.image_url || att.thumb_url) {
          const imageUrl = att.image_url || att.thumb_url;
          const proxiedSrc = getProxiedImageUrl(imageUrl);
          
          items.push(
            <a
              key={`att-${idx}`}
              href={att.original_url || att.image_url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-2"
            >
              <img
                src={proxiedSrc}
                alt={att.title || att.fallback || 'Attachment'}
                className="max-w-md max-h-80 rounded-lg border border-white/20 hover:border-purple-500/50 transition-colors object-contain bg-black/20"
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              {att.title && (
                <span className="text-xs text-gray-400 mt-1 block">{att.title}</span>
              )}
            </a>
          );
        }
        
        // Handle link attachments with preview
        if (att.title && att.title_link && !att.image_url) {
          items.push(
            <a
              key={`att-link-${idx}`}
              href={att.title_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block text-blue-400 hover:underline text-sm"
            >
              {att.title}
            </a>
          );
        }
        
        // Handle text-only attachments
        if (att.text && !att.image_url && !att.thumb_url) {
          items.push(
            <div key={`att-text-${idx}`} className="mt-2 pl-3 border-l-2 border-gray-600 text-sm text-gray-400">
              {att.text}
            </div>
          );
        }
        
        // Handle pretext
        if (att.pretext) {
          items.unshift(
            <div key={`att-pretext-${idx}`} className="text-sm text-gray-300 mt-2">
              {att.pretext}
            </div>
          );
        }
      });
    }
    
    return items.length > 0 ? <div className="mt-2">{items}</div> : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/20 p-4 bg-black/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
              <MessageSquare size={24} />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Slack</h1>
              <p className="text-sm text-gray-400">
                {status?.connected ? status.team_name : 'Not connected'}
              </p>
            </div>
          </div>
          
          {/* Connection Status */}
          <div className="flex items-center gap-4">
            {status?.connected ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 size={16} />
                <span>Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>Disconnected</span>
              </div>
            )}
            <button
              onClick={() => { loadStatus(); loadChannels(); loadUsers(); }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {!status?.connected ? (
        <div className="max-w-lg mx-auto mt-20 p-6 bg-black border border-white/20 rounded-xl text-center shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_25px_rgba(255,255,255,0.15)] transition-shadow">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Slack Not Connected</h2>
          <p className="text-gray-400 mb-4">
            {status?.error || 'Unable to connect to Slack. Please check your bot token.'}
          </p>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4">
          <div className="grid grid-cols-12 gap-4 h-[calc(100vh-140px)]">
            {/* Sidebar */}
            <div className="col-span-3 bg-black border border-white/20 rounded-xl overflow-hidden flex flex-col shadow-[0_0_15px_rgba(255,255,255,0.05)] hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-shadow">
              {/* Tab Switcher */}
              <div className="flex border-b border-white/20">
                <button
                  onClick={() => setActiveTab('channels')}
                  className={`flex-1 p-3 text-sm font-medium transition-colors ${
                    activeTab === 'channels' 
                      ? 'bg-purple-500/20 text-purple-300 border-b-2 border-purple-500' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Hash size={14} className="inline mr-1" />
                  Channels
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`flex-1 p-3 text-sm font-medium transition-colors ${
                    activeTab === 'users' 
                      ? 'bg-purple-500/20 text-purple-300 border-b-2 border-purple-500' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Users size={14} className="inline mr-1" />
                  Members
                </button>
              </div>
              
              {/* Sort/Filter Bar */}
              {activeTab === 'channels' ? (
                <div className="p-2 border-b border-white/20 flex items-center gap-2">
                  {/* Sort Dropdown */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      <span className="flex items-center gap-1">
                        <Filter size={12} />
                        {channelSort === 'favorites' && 'Favorites First'}
                        {channelSort === 'a-z' && 'A → Z'}
                        {channelSort === 'z-a' && 'Z → A'}
                        {channelSort === 'newest' && 'Newest'}
                        {channelSort === 'oldest' && 'Oldest'}
                      </span>
                      <ChevronDown size={12} />
                    </button>
                    {showSortMenu && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-white/20 rounded-lg shadow-xl z-10 overflow-hidden">
                        {[
                          { value: 'favorites', label: 'Favorites First', icon: Star },
                          { value: 'a-z', label: 'A → Z', icon: SortAsc },
                          { value: 'z-a', label: 'Z → A', icon: SortDesc },
                          { value: 'newest', label: 'Newest First', icon: SortDesc },
                          { value: 'oldest', label: 'Oldest First', icon: SortAsc },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { setChannelSort(opt.value); setShowSortMenu(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/10 transition-colors ${
                              channelSort === opt.value ? 'text-purple-400 bg-purple-500/10' : 'text-gray-300'
                            }`}
                          >
                            <opt.icon size={12} />
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Show Hidden Toggle */}
                  <button
                    onClick={() => setShowHidden(!showHidden)}
                    className={`p-1.5 rounded transition-colors ${
                      showHidden ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-white hover:bg-white/10'
                    }`}
                    title={showHidden ? 'Hide hidden channels' : 'Show hidden channels'}
                  >
                    {showHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </div>
              ) : (
                <div className="p-2 border-b border-white/20">
                  {/* Member Sort Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowMemberSortMenu(!showMemberSortMenu)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-xs text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded transition-colors"
                    >
                      <span className="flex items-center gap-1">
                        <Filter size={12} />
                        {memberSort === 'a-z' && 'A → Z'}
                        {memberSort === 'z-a' && 'Z → A'}
                      </span>
                      <ChevronDown size={12} />
                    </button>
                    {showMemberSortMenu && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-white/20 rounded-lg shadow-xl z-10 overflow-hidden">
                        {[
                          { value: 'a-z', label: 'A → Z', icon: SortAsc },
                          { value: 'z-a', label: 'Z → A', icon: SortDesc },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => { setMemberSort(opt.value); setShowMemberSortMenu(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/10 transition-colors ${
                              memberSort === opt.value ? 'text-purple-400 bg-purple-500/10' : 'text-gray-300'
                            }`}
                          >
                            <opt.icon size={12} />
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* List */}
              <div className="flex-1 overflow-y-auto p-2">
                {activeTab === 'channels' ? (
                  <div className="space-y-1">
                    {sortedChannels.map(channel => {
                      const isHidden = hiddenChannels.includes(channel.id);
                      const isFavorite = favoriteChannels.includes(channel.id);
                      
                      return (
                        <div
                          key={channel.id}
                          className={`group relative rounded-lg transition-all duration-200 ${
                            selectedChannel?.id === channel.id
                              ? 'bg-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.3)]'
                              : 'hover:bg-white/5 hover:shadow-[0_0_10px_rgba(255,255,255,0.1)]'
                          } ${isHidden ? 'opacity-50' : ''}`}
                        >
                          <button
                            onClick={() => selectChannel(channel)}
                            className={`w-full text-left p-2 flex items-center gap-2 ${
                              selectedChannel?.id === channel.id ? 'text-white' : 'text-gray-300'
                            }`}
                          >
                            {isFavorite && (
                              <Star size={12} className="text-yellow-500 fill-yellow-500 flex-shrink-0" />
                            )}
                            <Hash size={16} className="text-gray-500 flex-shrink-0" />
                            <span className="truncate flex-1">{channel.name}</span>
                            {channel.num_members > 0 && (
                              <span className="text-xs text-gray-500">{channel.num_members}</span>
                            )}
                          </button>
                          
                          {/* Hover Actions */}
                          <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 bg-[#0a0a0a] rounded">
                            <button
                              onClick={(e) => toggleFavoriteChannel(channel.id, e)}
                              className={`p-1 rounded hover:bg-white/10 transition-colors ${
                                isFavorite ? 'text-yellow-500' : 'text-gray-500 hover:text-yellow-400'
                              }`}
                              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              <Star size={12} className={isFavorite ? 'fill-current' : ''} />
                            </button>
                            <button
                              onClick={(e) => toggleHideChannel(channel.id, e)}
                              className={`p-1 rounded hover:bg-white/10 transition-colors ${
                                isHidden ? 'text-purple-400' : 'text-gray-500 hover:text-gray-300'
                              }`}
                              title={isHidden ? 'Show channel' : 'Hide channel'}
                            >
                              {isHidden ? <Eye size={12} /> : <EyeOff size={12} />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {sortedChannels.length === 0 && (
                      <p className="text-gray-500 text-sm p-2">
                        {channels.length === 0 ? 'No channels found' : 'All channels hidden'}
                      </p>
                    )}
                    {hiddenChannels.length > 0 && !showHidden && (
                      <p className="text-gray-600 text-xs p-2 text-center">
                        {hiddenChannels.length} channel{hiddenChannels.length > 1 ? 's' : ''} hidden
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sortedMembers.map(user => (
                      <div
                        key={user.id}
                        className="p-2 rounded-lg hover:bg-white/5 hover:shadow-[0_0_10px_rgba(255,255,255,0.05)] flex items-center gap-2 transition-all duration-200"
                      >
                        {user.avatar ? (
                          <img 
                            src={user.avatar} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-8 h-8 rounded-full bg-purple-500/30 items-center justify-center flex-shrink-0 ${user.avatar ? 'hidden' : 'flex'}`}
                        >
                          <User size={14} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{user.real_name || user.name}</p>
                          {user.title && (
                            <p className="text-xs text-gray-500 truncate">{user.title}</p>
                          )}
                        </div>
                        {user.status_emoji && (
                          <span className="text-sm">{user.status_emoji}</span>
                        )}
                      </div>
                    ))}
                    {users.length === 0 && (
                      <p className="text-gray-500 text-sm p-2">No members found</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Main Content - Messages */}
            <div className={`${activeThread ? 'col-span-5' : 'col-span-9'} bg-black border border-white/20 rounded-xl overflow-hidden flex flex-col transition-all duration-300 shadow-[0_0_15px_rgba(255,255,255,0.05)] hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]`}>
              {selectedChannel ? (
                <>
                  {/* Channel Header */}
                  <div className="p-4 border-b border-white/20 flex items-center gap-3">
                    <Hash size={20} className="text-gray-400" />
                    <div>
                      <h2 className="font-semibold">{selectedChannel.name}</h2>
                      {selectedChannel.topic && (
                        <p className="text-xs text-gray-500 truncate">{selectedChannel.topic}</p>
                      )}
                    </div>
                    <button
                      onClick={() => loadMessages(selectedChannel.id)}
                      className="ml-auto p-2 hover:bg-white/10 rounded-lg"
                      disabled={loadingMessages}
                    >
                      <RefreshCw size={16} className={loadingMessages ? 'animate-spin' : ''} />
                    </button>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {loadingMessages ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="text-center">
                          <p>No messages in this channel</p>
                          <p className="text-xs mt-2">Invite the bot to this channel in Slack to see messages</p>
                        </div>
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const avatar = getUserAvatar(msg.user);
                        const isBotMessage = msg.user === status?.bot_user_id;
                        return (
                          <div key={msg.ts || idx} className="flex gap-3 hover:bg-white/5 hover:shadow-[0_0_10px_rgba(255,255,255,0.05)] p-2 rounded-lg -mx-2 group relative transition-all duration-200">
                            {/* Delete button - appears on hover for bot messages */}
                            {isBotMessage && (
                              <button
                                onClick={() => deleteMessage(msg.ts)}
                                className="absolute right-2 top-2 p-1.5 bg-red-500/20 hover:bg-red-500/40 border border-red-500/30 rounded opacity-0 group-hover:opacity-100 transition-all text-red-400 hover:text-red-300"
                                title="Delete message"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                            
                            {/* Avatar */}
                            {avatar ? (
                              <img
                                src={avatar}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                <User size={18} className="text-white" />
                              </div>
                            )}
                            
                            <div className="min-w-0 flex-1">
                              {/* Name and timestamp */}
                              <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-sm text-white">
                                  {getUserName(msg.user)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatTimestamp(msg.ts)}
                                </span>
                                {msg.edited && (
                                  <span className="text-xs text-gray-600">(edited)</span>
                                )}
                              </div>
                              
                              {/* Message text */}
                              <div className="text-gray-200 text-sm whitespace-pre-wrap break-words leading-relaxed">
                                {renderMessageText(msg.text)}
                              </div>
                              
                              {/* Attachments and files */}
                              {renderAttachments(msg)}
                              
                              {/* Reactions */}
                              {msg.reactions?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {msg.reactions.map((r, i) => (
                                    <span 
                                      key={i} 
                                      className="text-sm bg-white/10 hover:bg-white/20 px-2 py-1 rounded-full flex items-center gap-1.5 cursor-default transition-colors"
                                      title={`:${r.name}:`}
                                    >
                                      <span className="text-base">{getEmoji(r.name)}</span>
                                      <span className="text-gray-400 text-xs">{r.count}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* Thread indicator */}
                              {msg.reply_count > 0 && (
                                <button 
                                  onClick={() => loadThread(selectedChannel.id, msg.ts)}
                                  className="mt-2 text-xs text-blue-400 hover:underline cursor-pointer flex items-center gap-1"
                                >
                                  <MessageSquare size={12} />
                                  {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-white/20">
                    {/* Attachment Preview */}
                    {attachedFile && (
                      <div className="mb-3 p-3 bg-white/5 border border-white/20 rounded-lg">
                        <div className="flex items-start gap-3">
                          {attachedFilePreview ? (
                            <img 
                              src={attachedFilePreview} 
                              alt="Preview" 
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-purple-500/20 rounded-lg flex items-center justify-center">
                              <Paperclip size={24} className="text-purple-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{attachedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(attachedFile.size / 1024).toFixed(1)} KB
                            </p>
                            {sending && uploadProgress > 0 && (
                              <div className="mt-2">
                                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-purple-500 transition-all duration-300"
                                    style={{ width: `${uploadProgress}%` }}
                                  />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{uploadProgress}% uploaded</p>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={removeAttachment}
                            className="p-1 hover:bg-white/10 rounded transition-colors text-gray-400 hover:text-white"
                            disabled={sending}
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2 relative">
                      {/* Hidden file input */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.txt,.zip"
                      />
                      
                      {/* Attachment button */}
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        className="p-2.5 bg-black border border-white/20 rounded-lg hover:bg-white/5 hover:border-white/30 disabled:opacity-50 transition-colors"
                        title="Attach file"
                      >
                        <Paperclip size={18} className="text-gray-400" />
                      </button>
                      
                      {/* Message input with mention picker */}
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          ref={messageInputRef}
                          value={newMessage}
                          onChange={handleMessageChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !showMentionPicker) {
                              sendMessage();
                            } else if (e.key === 'Escape') {
                              setShowMentionPicker(false);
                            }
                          }}
                          placeholder={attachedFile ? 'Add a message... (type @ to mention)' : `Message #${selectedChannel.name} (type @ to mention)`}
                          className="w-full bg-black border border-white/20 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500 transition-colors"
                          disabled={sending}
                        />
                        
                        {/* Mention Picker Dropdown */}
                        {showMentionPicker && mentionFilteredUsers.length > 0 && (
                          <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#111] border border-white/20 rounded-lg shadow-xl max-h-64 overflow-y-auto z-20">
                            <div className="p-2 border-b border-white/20 text-xs text-gray-400">
                              Select a person to mention
                            </div>
                            {mentionFilteredUsers.map(user => (
                              <button
                                key={user.id}
                                onClick={() => insertMention(user)}
                                className="w-full flex items-center gap-3 p-2 hover:bg-white/10 transition-colors text-left"
                              >
                                {user.avatar ? (
                                  <img src={user.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-purple-500/30 flex items-center justify-center">
                                    <User size={14} />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium truncate">{user.real_name || user.name}</p>
                                  {user.title && (
                                    <p className="text-xs text-gray-500 truncate">{user.title}</p>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={sendMessage}
                        disabled={sending || (!newMessage.trim() && !attachedFile)}
                        className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                      >
                        {sending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Send size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Hash size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Select a channel to view messages</p>
                  </div>
                </div>
              )}
            </div>

            {/* Thread Panel */}
            {activeThread && (
              <div className="col-span-4 bg-black border border-white/20 rounded-xl overflow-hidden flex flex-col shadow-[0_0_15px_rgba(255,255,255,0.05)] hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-shadow">
                {/* Thread Header */}
                <div className="p-4 border-b border-white/20 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">Thread</h3>
                    <p className="text-xs text-gray-500">
                      {threadMessages.length > 0 ? `${threadMessages.length} ${threadMessages.length === 1 ? 'message' : 'messages'}` : 'Loading...'}
                    </p>
                  </div>
                  <button
                    onClick={closeThread}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Thread Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {loadingThread ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                    </div>
                  ) : threadMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <p className="text-sm">No replies yet</p>
                    </div>
                  ) : (
                    threadMessages.map((msg, idx) => {
                      const avatar = getUserAvatar(msg.user);
                      return (
                        <div key={msg.ts || idx} className="flex gap-3 hover:bg-white/5 p-2 rounded-lg -mx-2">
                          {avatar ? (
                            <img src={avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-purple-500/30 flex items-center justify-center flex-shrink-0">
                              <User size={14} />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="font-medium text-sm">
                                {userMap[msg.user]?.real_name || userMap[msg.user]?.name || msg.user || 'Unknown'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {msg.ts && new Date(parseFloat(msg.ts) * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                              {renderMessageText(msg.text)}
                            </div>
                            
                            {/* Attachments in thread - use same rendering as main messages */}
                            {renderAttachments(msg)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Thread Reply Input */}
                <div className="p-4 border-t border-white/20">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={threadReply}
                      onChange={(e) => setThreadReply(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendThreadReply()}
                      placeholder="Reply in thread..."
                      className="flex-1 bg-black border border-white/20 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-purple-500/50 placeholder:text-gray-500"
                    />
                    <button
                      onClick={sendThreadReply}
                      disabled={sending || !threadReply.trim()}
                      className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                    >
                      {sending ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Send size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
